import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { apiErr } from "./SuperHeader";

export default function StatusAction({ company, onDone, size = "sm" }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  const suspended = company.status === "suspended";
  const action = suspended ? "activate" : "suspend";

  const run = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/admin/companies/${company.id}/${action}`, suspended ? {} : { reason: reason || null });
      toast.success(suspended ? `${company.name} activated` : `${company.name} suspended`);
      setOpen(false); setReason("");
      onDone?.();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Button size={size} variant="outline" onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className={suspended ? "text-emerald-700 hover:bg-emerald-50" : "text-rose-700 hover:bg-rose-50"}
        data-testid={`${action}-${company.id}`}>
        {suspended ? "Activate" : "Suspend"}
      </Button>
      <AlertDialog open={open} onOpenChange={(v) => !busy && setOpen(v)}>
        <AlertDialogContent className="bg-white" onClick={(e) => e.stopPropagation()} data-testid="status-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{suspended ? "Activate" : "Suspend"} {company.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              {suspended
                ? "Users of this company will be able to sign in again. Paused campaigns stay paused until the company restarts them."
                : "All users of this company will be signed out and blocked, running campaigns will be paused, and AI replies will stop. No data is deleted."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {!suspended && (
            <Textarea placeholder="Reason (optional, saved in the activity log)" value={reason} maxLength={300}
              onChange={(e) => setReason(e.target.value)} data-testid="suspend-reason-input" />
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy} data-testid="status-cancel">Cancel</AlertDialogCancel>
            <Button onClick={run} disabled={busy} data-testid="status-confirm"
              className={suspended ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"}>
              {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}{suspended ? "Activate" : "Suspend"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
